// resolve.ts - turn a committed decision (street, action, read tag, optional raise amount) into a fully
// resolved two-axis grade. This is the deterministic grade-resolution logic, transcribed from the mockup's
// commitAction (docs/ui-explorations/local-mode-read-first-v2.html lines 1074-1099): action-right ->
// best (a correct raise outside the sizing window downgrades to exc), wrong -> the branch's move class;
// read-right (tagged Vacuum) -> exc, else inacc; the verdict combines the two axes.
//
// The dx5 payload it reads is AUTHORED (data.ts, §5). The identical resolution runs over a GENERATED
// hand in S24 by feeding an engine GradeBand (via MOVE_CLASS_FROM_BAND) + a CoachText instead of the
// authored branch - same <GradeReveal>, same shape.
import type { PlayerTag } from '../diagnose/data';
import { DIAGNOSE_GRADES, READ_AXIS } from './data';
import { MOVE_CLASS_LABELS, type MoveClass, type ResolvedGrade } from './types';

type ActionKey = 'fold' | 'call' | 'raise';

/** Check + cross marks (mockup used &#10003; / &#10005;) - text symbols, not emoji. */
const MARK_CHECK = '✓';
const MARK_CROSS = '✕';

/** Resolve the reveal for a decision on `street`. `amount` is the typed raise dollars (digits only used). */
export function resolveGrade(
  street: number,
  action: ActionKey,
  tag: PlayerTag | null,
  amount?: string,
): ResolvedGrade {
  const g = DIAGNOSE_GRADES[street];
  const actionRight = action === g.correct;

  // --- Axis A: the hand / the play ---
  let handCls: MoveClass;
  let handLabel: string;
  let handText: string;
  if (actionRight) {
    handCls = 'best';
    handLabel = g.right.handLabel;
    handText = g.right.handText;
    // a correct raise typed outside the sizing window still works, but is only "Excellent".
    if (action === 'raise' && g.raiseWindow) {
      const n = Number(String(amount ?? '').replace(/[^0-9.]/g, ''));
      if (n && (n < g.raiseWindow.lo || n > g.raiseWindow.hi)) {
        handCls = 'exc';
        handText += ` Your size of $${n} works, though $${g.raiseWindow.lo} to $${g.raiseWindow.hi} is the tightest window.`;
      }
    }
  } else {
    const w = g.wrong[action];
    // every diverging action a street offers has a branch; fall back defensively.
    handCls = w?.cls ?? 'mist';
    handLabel = w?.handLabel ?? action;
    handText = w?.handText ?? '';
  }

  // --- Axis B: the read / the game ---
  const readRight = tag === 'Vacuum';
  const readCls = readRight ? READ_AXIS.right.cls : READ_AXIS.wrong.cls;
  const readLabel = readRight ? READ_AXIS.right.label : READ_AXIS.wrong.label;
  const readText = readRight
    ? READ_AXIS.right.text
    : READ_AXIS.wrong.text.replace('{tag}', tag ?? 'nothing yet');

  // --- verdict (combines both axes) ---
  const good = actionRight && readRight;
  const verdictCls: MoveClass = good ? 'best' : actionRight || readRight ? 'inacc' : 'blun';
  const verdictLabel = good
    ? 'Well read, well played'
    : actionRight
      ? 'Right play, read a little off'
      : readRight
        ? 'Right read, wrong play'
        : 'Missed the read and the play';
  const verdictSub = good
    ? 'The read and the action lined up. That is the whole game.'
    : 'Graded on the read and the play, not on whether you won.';
  const mark = good ? MARK_CHECK : verdictCls === 'inacc' ? '!' : MARK_CROSS;

  // --- coach callout: good path uses the right coach; wrong path fires "Where it went wrong." ---
  const coach = actionRight
    ? { lead: 'Carry this forward.', text: g.right.coach, bad: false }
    : { lead: 'Where it went wrong.', text: g.wrong[action]?.coach ?? '', bad: true };

  return {
    verdict: { cls: verdictCls, mark, label: verdictLabel, sub: verdictSub },
    axisA: {
      badge: 'A',
      kicker: 'Your play, the hand',
      cls: handCls,
      label: handLabel,
      text: handText,
    },
    axisB: {
      badge: 'B',
      kicker: 'Your read, the game',
      cls: readCls,
      label: readLabel,
      text: readText,
    },
    coach,
    // the deep debrief always teaches the correct line, regardless of what the user chose.
    debrief: g.right.debrief,
  };
}

/** The colored heading text: "Best . Raise to isolate". Exported for the reveal + tests. */
export function classHeading(cls: MoveClass, label: string): string {
  return `${MOVE_CLASS_LABELS[cls]} . ${label}`;
}

/** The correct-line grade for a street: the ideal play (a mid-window raise where one applies) with the
 *  villain read right (Vacuum) - a genuine `resolveGrade` run, not a fixture. Used to fill streets the
 *  user did not play through when the end-of-hand report is reached, e.g. the `?view=report` deep-link,
 *  so the report always shows four real grades and the correct line reads Best -> 100 everywhere. */
export function canonicalGrade(street: number): ResolvedGrade {
  const g = DIAGNOSE_GRADES[street];
  const amount = g.raiseWindow
    ? String(Math.round((g.raiseWindow.lo + g.raiseWindow.hi) / 2))
    : undefined;
  return resolveGrade(street, g.correct, 'Vacuum', amount);
}
